import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";

interface PaginationProps {
  startItem: number;
  endItem: number;
  total: number;
  totalPages: number;
  page: number;
  loading: boolean;
  handlePageChange: (direction: 'prev' | 'next') => void;
  /**
   * Optional - when provided, a numeric page input is rendered between the "info" label
   * and the prev/next controls. Other usages (GPSR, Orders, Licenses) omit this prop
   * and keep the arrows-only layout they had before.
   */
  onGotoPage?: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  startItem,
  endItem,
  total,
  totalPages,
  page,
  loading,
  handlePageChange,
  onGotoPage,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>(String(page));

  // Keep the input in sync when the page changes from outside (arrows, filter reset, …).
  useEffect(() => {
    setInputValue(String(page));
  }, [page]);

  const commit = () => {
    if (!onGotoPage) return;
    const parsed = parseInt(inputValue, 10);
    if (!Number.isFinite(parsed) || Number.isNaN(parsed)) {
      setInputValue(String(page));
      return;
    }
    const clamped = Math.min(Math.max(1, parsed), Math.max(1, totalPages));
    if (clamped !== page) {
      onGotoPage(clamped);
    }
    setInputValue(String(clamped));
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setInputValue(String(page));
      (event.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className="pagination">
      <div className="pagination-info">
        {t('pagination_of', { start: startItem, end: endItem, total })}
      </div>
      {onGotoPage && (
        <div className="pagination-goto">
          <label className="pagination-goto__label" htmlFor="pagination-goto-input">
            {t('pagination_page_label')}
          </label>
          <input
            id="pagination-goto-input"
            className="pagination-goto__input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={inputValue}
            disabled={loading || totalPages <= 1}
            onChange={(e) => setInputValue(e.target.value.replace(/\D/g, ''))}
            onBlur={commit}
            onKeyDown={handleInputKeyDown}
            aria-label={t('pagination_goto_aria', { total: totalPages })}
          />
          <span className="pagination-goto__total">
            {t('pagination_goto_of', { total: totalPages })}
          </span>
        </div>
      )}
      <div className="pagination-controls">
        <button
          className="btn-icon"
          disabled={page === 1 || loading}
          onClick={() => handlePageChange('prev')}
          title={t('pagination_prev')}
          aria-label={t('pagination_prev')}
        >
          <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 9L1 5L5 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button
          className="btn-icon"
          disabled={page === totalPages || loading}
          onClick={() => handlePageChange('next')}
          title={t('pagination_next')}
          aria-label={t('pagination_next')}
        >
          <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L5 5L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Pagination;
